import React from 'react';

import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Checkbox,
  FormControlLabel,
  InputAdornment,
  Stack,
  TextField,
} from '@mui/material';

import PageSubheader from '~/components/Common/Elements/PageSubheader';
import { useUpdateExperienceValues } from '~/components/Experiences/hooks';

import { EXP_PROVIDER_REZDY, ORIGINAL_EXPERIENCE_CONTENT_TAB } from '~/consts/experiences';

import { MemberDiscountOverrides } from '~/services/ExperiencesService';

function ExperienceCurationMemberDiscount() {
  const { contentTab, updateValues, values } = useUpdateExperienceValues();

  const areOriginalValues = contentTab === ORIGINAL_EXPERIENCE_CONTENT_TAB;

  // For now member discount will be available for Rezdy experiences. Later we will include Klook as well,
  // but we want to roll out the feature gradually starting with Rezdy.
  if (values?.provider !== EXP_PROVIDER_REZDY) {
    return null;
  }

  const memberDiscountEnabled = values?.memberDiscountEnabled ?? true;
  const overrides: MemberDiscountOverrides = values?.memberDiscountOverrides ?? {};

  const handleOverrideChange = (field: keyof MemberDiscountOverrides, value: number | boolean | undefined) => {
    const updated: MemberDiscountOverrides = { ...overrides };
    if (value === undefined) {
      delete updated[field];
    } else {
      Object.assign(updated, { [field]: value });
    }
    updateValues({ memberDiscountOverrides: Object.keys(updated).length > 0 ? updated : undefined });
  };

  // forceEnable and forceDisable are mutually exclusive — checking one clears the other
  const handleForceToggle = (field: 'forceEnable' | 'forceDisable', checked: boolean) => {
    const updated: MemberDiscountOverrides = { ...overrides };
    delete updated.forceEnable;
    delete updated.forceDisable;
    if (checked) {
      Object.assign(updated, { [field]: true });
    }
    updateValues({ memberDiscountOverrides: Object.keys(updated).length > 0 ? updated : undefined });
  };

  const parseOptionalNumber = (raw: string): number | undefined => {
    const n = parseFloat(raw);
    return isNaN(n) ? undefined : n;
  };

  return (
    <Box mt={2}>
      <PageSubheader title="LuxPlus Member Discount" />

      <FormControlLabel
        control={
          <Checkbox
            checked={memberDiscountEnabled}
            onChange={(e) => updateValues({ memberDiscountEnabled: e.target.checked })}
            disabled={areOriginalValues}
          />
        }
        label="Member Discount Enabled"
      />

      <Accordion variant="outlined">
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>MEMBER DISCOUNT OVERRIDES</AccordionSummary>
        <AccordionDetails>
          <Stack spacing={2}>
            <TextField
              label="Margin Override"
              type="number"
              disabled={areOriginalValues}
              value={overrides.marginOverride ?? ''}
              onChange={(e) => handleOverrideChange('marginOverride', parseOptionalNumber(e.target.value))}
              InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
              helperText="Replace formula margin with a fixed percentage"
              fullWidth
            />

            <TextField
              label="Floor Override"
              type="number"
              disabled={areOriginalValues}
              value={overrides.floorOverride ?? ''}
              onChange={(e) => handleOverrideChange('floorOverride', parseOptionalNumber(e.target.value))}
              helperText="Replace the margin-floor constant"
              fullWidth
            />

            <TextField
              label="Max Discount Cap"
              type="number"
              disabled={areOriginalValues}
              value={overrides.maxDiscountCapPct ?? ''}
              onChange={(e) => handleOverrideChange('maxDiscountCapPct', parseOptionalNumber(e.target.value))}
              InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
              helperText="Cap the effective member discount percentage"
              fullWidth
            />

            <FormControlLabel
              control={
                <Checkbox
                  checked={overrides.forceEnable ?? false}
                  onChange={(e) => handleForceToggle('forceEnable', e.target.checked)}
                  disabled={areOriginalValues}
                />
              }
              label="Force Enable (bypass minimum-saving threshold)"
            />

            <FormControlLabel
              control={
                <Checkbox
                  checked={overrides.forceDisable ?? false}
                  onChange={(e) => handleForceToggle('forceDisable', e.target.checked)}
                  disabled={areOriginalValues}
                />
              }
              label="Force Disable (hard-disable regardless of formula result)"
            />
          </Stack>
        </AccordionDetails>
      </Accordion>
    </Box>
  );
}

export default ExperienceCurationMemberDiscount;
